import * as React from 'react';
import { Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import HTag from './HTag';
import { computed, observable } from 'mobx';
import { LinkFilter } from '../models/configuration/LinkFilter';
import DynamicLink from './DynamicLink';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { AccessibilityStore, DataStore, EntityStore } from '../stores';
import { DynamicLinkConfig } from '../models/configuration/components/DynamicLinkConfig';
import { Theme } from '../models/Theme';
import { FundManagers as FundManagersConfig } from '../models/configuration/components/FundManagers';
import { ShowExtended } from '../models/configuration/ShowExtended';
import { Link } from '../models/configuration/Link';
import { getExtendedValue } from '../utils/getExtendedValue';
import { FundManager } from '../models/entity';

const FundManagerExtendedItems = styled<any, any>(
    'ul',
    'fundManagerExtendedItems',
    'fundManagersContent'
)`
    ${propStyles};
`;

FundManagerExtendedItems.defaultProps = {
    listStyle: 'none',
};

const FundManagerExtendedItem = styled<any, any>(
    'li',
    'fundManagerExtendedItem',
    'fundManagersContent'
)`
    ${propStyles};
`;

FundManagerExtendedItem.defaultProps = {};

const FundManagerExtendedItemText = styled<any, any>(
    'p',
    'fundManagerExtendedItemText',
    'fundManagersContent'
)`
    ${propStyles};
`;

FundManagerExtendedItemText.defaultProps = {};

const FundManagerBiography = styled<any, any>(
    'p',
    'fundManagerBiography',
    'fundManagersContent'
)`
    ${propStyles};
`;

FundManagerBiography.defaultProps = {
    color: 'body',
    fontFamily: 'body',
    fontWeight: 'regular',
    fontSize: 'sm',
    marginY: 'sm',
};

const FundManagerLink = styled<any, any>(
    'a',
    'fundManagerLink',
    'fundManagersContent'
)`
    ${propStyles};
`;

FundManagerLink.defaultProps = {
    display: 'block',
    color: 'link',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'xs',
    hover: {
        color: 'linkHover',
    },
};

interface FundManagerContentProps {
    theme?: Theme;
    fundManager: FundManager;
    accessibilityStore?: AccessibilityStore;
    dataStore?: DataStore;
    entityStore?: EntityStore;
    config?: FundManagersConfig;
}

@inject('dataStore', 'accessibilityStore', 'entityStore')
@observer
class FundManagersContent extends React.Component<FundManagerContentProps> {
    autoPlayId: number;
    @observable activeSlide: number = 0;

    @computed
    get fundManagersConfig(): FundManagersConfig {
        return this.props.config;
    }

    @computed
    get clientCode() {
        const { entityStore } = this.props;
        return entityStore.value.clientCode;
    }

    @computed
    get link(): Link {
        const { link } = this.fundManagersConfig;
        return link;
    }

    @computed
    get url(): LinkFilter {
        const { url } = this.fundManagersConfig;
        return url;
    }

    @computed
    get label(): string {
        const { label } = this.fundManagersConfig;
        return label;
    }

    @computed
    get dynamicLinkConfig(): DynamicLinkConfig {
        const url = this.url;
        return {
            url,
        };
    }

    @computed
    get dynamicLink(): React.ReactNode {
        const { theme, fundManager } = this.props;

        return (
            <DynamicLink
                config={this.dynamicLinkConfig}
                {...theme.FundCardLink}
                fundManager={fundManager}
                metricContext={`fundManager-${camelCase(this.clientCode)}`}
                ariaLabelKey={'fundManagerLink'}
                onClick={this.onClick}
            >
                <Translate>{this.link.label}</Translate>
                {/* The url will pull the links text from the links config */}
            </DynamicLink>
        );
    }

    @computed
    get fundManagerLink(): React.ReactNode {
        const { theme, accessibilityStore, fundManager } = this.props;

        if (this.link) {
            const link = {
                label: this.link.label,
                value: getExtendedValue(
                    this.link.property,
                    fundManager.extended
                ),
            };

            return (
                <FundManagerLink
                    href={link.value}
                    onClick={() => this.onClick(link.value)}
                    data-metric-id={`fundManager-link-${this.clientCode}`}
                    aria-label={accessibilityStore.linkAccessibilityValue(
                        'fundManagerLink'
                    )}
                    {...theme.FundManagerLink}
                >
                    <Translate>{link.label}</Translate>
                </FundManagerLink>
            );
        }
    }

    @computed
    get linkComponent(): React.ReactNode {
        if (this.url) {
            return this.dynamicLink;
        } else {
            return this.fundManagerLink;
        }
    }

    @computed
    get biography(): string {
        const { fundManager, dataStore } = this.props;

        let bio = '';
        if (this.showBiography) {
            const biographyData =
                fundManager.biography.find(
                    fundBio => fundBio.culture === dataStore.culture
                ) ||
                fundManager.biography.find(
                    fundBio =>
                        fundBio.culture === dataStore.config.defaultCulture
                );

            if (biographyData) {
                const { biography } = biographyData;
                if (biography) {
                    bio = biography;
                }
            }
        }
        return bio;
    }

    @computed
    get showBiography(): boolean {
        const { showBiography } = this.fundManagersConfig;
        return showBiography;
    }

    @computed
    get extended() {
        const { fundManager } = this.props;

        let extended = [];
        if (this.showExtended.length > 0) {
            extended = this.showExtended.map(extendedConfig => ({
                label: extendedConfig.label,
                value: getExtendedValue(
                    extendedConfig.property,
                    fundManager.extended
                ),
            }));
        }
        return extended;
    }

    @computed
    get showExtended(): Array<ShowExtended> {
        const { showExtended = [] } = this.fundManagersConfig;
        return showExtended;
    }

    onClick = (link: string) => {
        const { dataStore, fundManager } = this.props;
        dataStore.logDataLayer('redirect_to_fund_manager_bio', {
            fundManager: (fundManager as any).context || fundManager,
            clientCode: this.clientCode,
            redirectLink: link,
        });
    };

    render() {
        const { fundManager, theme } = this.props;

        return (
            <Fragment>
                <HTag
                    componentTheme={'FundManagerName'}
                    defaultStyleProps={{
                        color: 'body',
                        fontFamily: 'heading',
                        fontWeight: 'bold',
                        fontSize: 'sm',
                        textAlign: ['left', 'center'],
                        marginY: 'sm',
                    }}
                    level={4}
                    text={fundManager.name}
                />
                {this.extended.length > 0 && (
                    <FundManagerExtendedItems
                        {...theme.FundManagerExtendedItems}
                    >
                        {this.extended.map((extended, extendedIndex) => (
                            <FundManagerExtendedItem
                                key={extendedIndex}
                                {...theme.FundManagerExtendedItem}
                            >
                                {extended.label && (
                                    <FundManagerExtendedItemText
                                        {...theme.FundManagerExtendedItemLabel}
                                    >
                                        <Translate>{extended.label}</Translate>
                                    </FundManagerExtendedItemText>
                                )}
                                <FundManagerExtendedItemText
                                    {...theme.FundManagerExtendedItemValue}
                                >
                                    <Translate>{extended.value}</Translate>
                                </FundManagerExtendedItemText>
                            </FundManagerExtendedItem>
                        ))}
                    </FundManagerExtendedItems>
                )}
                {this.showBiography && (
                    <FundManagerBiography {...theme.FundManagerBiography}>
                        {this.biography}
                    </FundManagerBiography>
                )}
                {this.linkComponent}
            </Fragment>
        );
    }
}

export default withTheme(FundManagersContent) as any;



// WEBPACK FOOTER //
// ./src/Components/FundManagerContent.tsx